import { useEffect, useState } from 'react';

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@/ui';

import type {
  SeatChangePreview,
  SeatChangeReview,
  SeatChangeSchedule,
} from '@/types/memberSeatChange';

export const formatSeatChangeSchedule = (schedule: SeatChangeSchedule) => {
  const options: Intl.DateTimeFormatOptions = {
    timeZone: schedule.timezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const duration =
    (Date.parse(schedule.endAt) - Date.parse(schedule.startAt)) / 60000;
  return `${formatter.format(new Date(schedule.startAt))} to ${formatter.format(new Date(schedule.endAt))} (${schedule.timezone}; ${duration} minutes)`;
};

const statusLabels = {
  ready: 'Available',
  buffer_conflict: 'Buffer-only conflict',
  overlap_conflict:
    'Blocked: actual calendar overlap. Resolve the conflict before reviewing again.',
  unavailable:
    'Blocked: calendar availability could not be verified. Request a fresh review.',
  excluded: 'Excluded from this change',
};

export const canConfirmSeatChange = (
  preview: SeatChangePreview,
  waivers: string[],
) =>
  preview.canConfirm &&
  Date.parse(preview.expiresAt) > Date.now() &&
  preview.occurrences.every(
    (occurrence) =>
      occurrence.status === 'ready' ||
      occurrence.status === 'excluded' ||
      (occurrence.status === 'buffer_conflict' &&
        occurrence.canWaiveBuffer &&
        waivers.includes(occurrence.appointmentId)),
  );

export interface SeatChangeReviewDialogProps {
  preview: SeatChangePreview;
  saving: boolean;
  onCancel: () => void;
  onRefresh: () => void;
  onConfirm: (review: SeatChangeReview) => void;
}

const SeatChangeReviewDialog = ({
  preview,
  saving,
  onCancel,
  onRefresh,
  onConfirm,
}: SeatChangeReviewDialogProps) => {
  const [waivers, setWaivers] = useState<string[]>([]);
  const [expired, setExpired] = useState(
    Date.parse(preview.expiresAt) <= Date.now(),
  );
  useEffect(() => {
    const timer = setTimeout(
      () => {
        setExpired(true);
        setWaivers([]);
      },
      Math.max(0, Date.parse(preview.expiresAt) - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [preview.expiresAt]);
  return (
    <Dialog
      aria-labelledby="seat-change-review-title"
      maxWidth="md"
      fullWidth
      open
    >
      <DialogTitle id="seat-change-review-title">
        Review calendar changes
      </DialogTitle>
      <DialogContent>
        <Typography>
          The seat takes effect when saved. Calendar and session updates remain
          pending until verified.
        </Typography>
        <Typography>
          {preview.previousSeatType} to {preview.targetSeatType}
        </Typography>
        <Typography>{preview.excludedCount} excluded sessions</Typography>
        {!preview.occurrences.length && (
          <Typography>No eligible future native sessions to update.</Typography>
        )}
        {!preview.canConfirm && (
          <Typography role="alert">
            This change cannot be confirmed. Resolve blocked dates or request a
            fresh review.
          </Typography>
        )}
        {expired && (
          <Typography role="alert">
            This review expired. Request a fresh review.
          </Typography>
        )}
        {preview.occurrences.map((occurrence) => (
          <section
            key={occurrence.appointmentId}
            className="my-4 rounded-xl border border-solid border-cement-200 p-4"
          >
            <Typography>
              Original: {formatSeatChangeSchedule(occurrence.originalSchedule)}
            </Typography>
            <Typography>
              Target: {formatSeatChangeSchedule(occurrence.targetSchedule)}
            </Typography>
            <Typography>{statusLabels[occurrence.status]}</Typography>
            {occurrence.status === 'buffer_conflict' &&
              occurrence.canWaiveBuffer && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={waivers.includes(occurrence.appointmentId)}
                      disabled={saving || expired}
                      onChange={(_, checked) =>
                        setWaivers((previous) =>
                          checked
                            ? [...previous, occurrence.appointmentId]
                            : previous.filter(
                                (id) => id !== occurrence.appointmentId,
                              ),
                        )
                      }
                    />
                  }
                  label={`Waive buffer only for ${formatSeatChangeSchedule(occurrence.targetSchedule)} (${occurrence.bufferBeforeMinutes} minutes before, ${occurrence.bufferAfterMinutes} minutes after). Actual overlaps are never allowed.`}
                />
              )}
          </section>
        ))}
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} variant="outlined" onClick={onCancel}>
          Back to editing
        </Button>
        <Button disabled={saving} variant="outlined" onClick={onRefresh}>
          Request fresh review
        </Button>
        <Button
          disabled={
            saving || expired || !canConfirmSeatChange(preview, waivers)
          }
          loading={saving}
          variant="contained"
          onClick={() => {
            if (canConfirmSeatChange(preview, waivers))
              onConfirm({
                previewId: preview.previewId,
                previewDigest: preview.previewDigest,
                sourceRevision: preview.sourceRevision,
                bufferWaiverAppointmentIds: waivers,
              });
          }}
        >
          Confirm seat change
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeatChangeReviewDialog;
